/**
 * Installation de l'espace.
 *
 * Onze étapes, validables séparément. Les étapes d'import sont facultatives :
 * l'espace reste vierge si on les ignore, conformément au cahier des charges.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, api, type Onboarding } from '../api/client';
import { DESTINATIONS, type DestinationEtape } from '../domaine/installation';
import { oublierEtatInstallation } from '../domaine/installation-terminee';

const FACULTATIVES = new Set(['IMPORT_CATALOGUE', 'IMPORT_TIERS', 'IMPORT_MACHINES']);

/** Une étape inconnue du répertoire n'a ni lien ni explication : on ne devine pas. */
const destination = (code: string): DestinationEtape => DESTINATIONS[code] ?? {};

export function OnboardingPage() {
  const [parcours, setParcours] = useState<Onboarding | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);

  const charger = useCallback(async () => {
    try {
      setParcours(await api.onboarding());
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Chargement impossible.');
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  const valider = async (code: string, ignorer: boolean) => {
    setEnCours(code);
    try {
      await api.completeStep(code, ignorer);
      // Cocher la dernière étape fait disparaître « Installation » du menu :
      // l'état mémorisé doit donc être relu, sinon l'entrée resterait jusqu'au
      // prochain rechargement de page.
      oublierEtatInstallation();
      await charger();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Action impossible.');
    } finally {
      setEnCours(null);
    }
  };

  const pourcentage =
    parcours && parcours.progress.total > 0
      ? Math.round((parcours.progress.done / parcours.progress.total) * 100)
      : 0;

  return (
    <>
      <div className="entete-page">
        <div>
          <h1>Installation de l’espace</h1>
          <p>
            Votre espace est livré vierge. Ces étapes le configurent — celles d’import restent
            facultatives.
          </p>
        </div>
      </div>

      {erreur && <div className="message erreur">{erreur}</div>}

      {parcours === null ? (
        <div className="carte">
          <div className="vide">Chargement…</div>
        </div>
      ) : (
        <>
          <div className="carte">
            <div className="carte-corps">
              <strong>
                {parcours.progress.done} étape{parcours.progress.done > 1 ? 's' : ''} sur{' '}
                {parcours.progress.total}
              </strong>
              <div className="jauge">
                <div style={{ width: `${pourcentage}%` }} />
              </div>
              <div className="aide">
                {parcours.status === 'COMPLETED'
                  ? 'Installation terminée.'
                  : 'Installation en cours.'}
              </div>
            </div>
          </div>

          <div className="carte">
            <h2>Étapes</h2>
            {parcours.steps.map((etape) => {
              const faite = etape.status === 'COMPLETED' || etape.status === 'SKIPPED';
              const occupe = enCours === etape.code;
              return (
                <div key={etape.code} className={`etape${faite ? ' faite' : ''}`}>
                  <div className="numero">{faite ? '✓' : etape.position}</div>
                  <div className="libelle">
                    {etape.label}
                    {etape.status === 'SKIPPED' && <div className="aide">Ignorée</div>}
                    {/* Dire ce qui manque vaut mieux que proposer un bouton qui
                        ne configure rien : l'étape restait cochable à vide. */}
                    {!faite && destination(etape.code).indisponible && (
                      <div className="aide">{destination(etape.code).indisponible}</div>
                    )}
                  </div>
                  <div className="actions">
                    {!faite && (
                      <>
                        {destination(etape.code).lien && (
                          <Link className="bouton-lien discret" to={destination(etape.code).lien!}>
                            {destination(etape.code).libelleAction ?? 'Ouvrir'}
                          </Link>
                        )}
                        <button
                          className="discret secondaire"
                          disabled={occupe}
                          onClick={() => void valider(etape.code, false)}
                        >
                          Marquer faite
                        </button>
                        {FACULTATIVES.has(etape.code) && (
                          <button
                            className="discret secondaire"
                            disabled={occupe}
                            onClick={() => void valider(etape.code, true)}
                          >
                            Ignorer
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}
